import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "../../apiClient";
import { createAndDownloadFlyer } from "./pdf"; // NEW

/* =========================
   THEME (visual-only constants)
   ========================= */
const VANILLA = "#F7F3E7";
const NAVY_BG = "#0B1327";
const NAVY_CARD = "#0F1E42";
const NAVY_SOFT = "#162B5A";
const SLATE_TEXT = "#E9EEF8";
const MUTED = "#9BB0D0";

/* ========= mapping labels (Market only) ========= */
const FIELD_LABELS = {
  id: "Account / Property ID",
  owner: "Owner / Entity",
  address: "Property Address",
  county: "County / CAD",
  group_code: "Neighborhood / Area / IMA",
  property_type: "Property Type",
  units: "Units (MF only)",
  area: "Area (SF)",
  market_value: "Market/Appraised Value",
  year_built: "Year Built",
  // NEW:
  agent: "Agent / Representative",
  deed_date: "Deed Date",
};

const REQUIRED_NOTE =
  "Map either Units or Area AND the Market/Appraised Value column. (Assessed value is not used.)";

/* =========================
   COMPONENT
   ========================= */
export default function EUAnalyzer({ email, onClose, logout }) {
  // ---------- file + preview ----------
  const [file, setFile] = useState(null);
  const [columns, setColumns] = useState([]);
  const [guessed, setGuessed] = useState({});
  const [mapping, setMapping] = useState({
    id: null, owner: null, address: null, county: null,
    group_code: null, property_type: null,
    units: null, area: null, market_value: null, year_built: null,
    // NEW:
    agent: null, deed_date: null,
  });

  // ---------- analysis settings (store as HUMAN values) ----------
  const [threshold, setThreshold] = useState(10);     // %
  const [minUnits, setMinUnits] = useState(1);
  const [sizeTol, setSizeTol] = useState(25);         // %
  const [yearTol, setYearTol] = useState(10);         // years
  const [minComps, setMinComps] = useState(5);
  const [minSubjectValue, setMinSubjectValue] = useState(0); // $
  const [taxRate, setTaxRate] = useState(2.5);        // %

  // ---------- UI state ----------
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);
  const [selections, setSelections] = useState({}); // { [key]: boolean[] }

  // ---------- NEW: Sorting & Filters (results page) ----------
  const [searchText, setSearchText] = useState("");
  const [filterMinComps, setFilterMinComps] = useState("");
  const [filterMinOverPct, setFilterMinOverPct] = useState("");
  const [filterMinTotal, setFilterMinTotal] = useState("");
  const [filterMaxTotal, setFilterMaxTotal] = useState("");
  const [filterAgent, setFilterAgent] = useState("");
  const [filterCounty, setFilterCounty] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterGroup, setFilterGroup] = useState("");

  const SORT_OPTS = [
    { key: "overPct", label: "Over % (vs Median)" },
    { key: "totalValue", label: "Total Value" },
    { key: "perUnit", label: "Per-Unit (Subject)" },
    { key: "medianPerUnit", label: "Group Median Per-Unit" },
    { key: "size", label: "Size (Units/SF)" },
    { key: "year", label: "Year Built" },
    { key: "compsCount", label: "# of Comps (available)" },
    { key: "chosenCount", label: "# of Comps (selected)" },
    { key: "owner", label: "Owner (A→Z)" },
    { key: "agent", label: "Agent (A→Z)" },
    { key: "county", label: "County (A→Z)" },
    { key: "type", label: "Type (A→Z)" },
    { key: "group", label: "Group/Area (A→Z)" },
  ];
  const [sortKey, setSortKey] = useState("overPct");
  const [sortDir, setSortDir] = useState("desc"); // 'asc' | 'desc'

  function clearFilters() {
    setSearchText("");
    setFilterMinComps("");
    setFilterMinOverPct("");
    setFilterMinTotal("");
    setFilterMaxTotal("");
    setFilterAgent("");
    setFilterCounty("");
    setFilterType("");
    setFilterGroup("");
  }

  // ---------- carousel ----------
  const [slide, setSlide] = useState(0);
  const canNextFrom0 = columns.length > 0;
  const mappingValid = useMemo(() => {
    const hasMarket = !!mapping.market_value;
    const hasSize = !!(mapping.area || mapping.units);
    return hasMarket && hasSize;
  }, [mapping]);
  const canNextFrom1 = mappingValid;
  const canOpenAnalysis = !!result;

  const leftDisabled = slide === 0;
  const rightEnabled =
    slide === 0 ? canNextFrom0 :
    slide === 1 ? canNextFrom1 :
    slide === 2 ? canOpenAnalysis : false;

  useEffect(() => {
    const onKey = (e) => {
      if (busy) return;
      if (e.key === "ArrowRight") { e.preventDefault(); if (rightEnabled) goNext(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); goPrev(); }
    };
    window.addEventListener("keydown", onKey, { passive: false });
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, rightEnabled]);

  function goNext() {
    setSlide((s) => {
      if (s === 0 && !canNextFrom0) return s;
      if (s === 1 && !canNextFrom1) return s;
      if (s === 2 && !canOpenAnalysis) return s;
      return Math.min(s + 1, canOpenAnalysis ? 3 : 2);
    });
  }
  function goPrev() {
    setSlide((s) => Math.max(0, s - 1));
  }

  // ---------- preview to get columns (Upload) ----------
  async function previewColumns() {
    if (!file) { setError("Choose an Excel/CSV file first."); return; }
    setError(""); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/eu/preview", fd);
      const cols = Array.isArray(res.data?.columns) ? res.data.columns : [];
      setColumns(cols);
      setGuessed(res.data?.guessed_mapping || {});
      setResult(null);
      setExpandedKey(null);
      setSelections({});
      setSlide(1);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  function useGuessed() {
    if (!guessed) return;
    const m = { ...mapping };
    Object.keys(m).forEach((k) => (m[k] = guessed[k] || null));
    setMapping(m);
  }
  function setMapKey(k, v) { setMapping((prev) => ({ ...prev, [k]: v || null })); }
  function copyMappingJSON() { navigator.clipboard?.writeText(JSON.stringify(mapping, null, 2)); }
  async function loadMappingFromFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const obj = JSON.parse(await f.text());
      const m = { ...mapping };
      Object.keys(m).forEach((k) => (m[k] = obj[k] || null));
      setMapping(m);
    } catch { setError("Invalid mapping JSON file"); }
  }

  // ---------- run analysis (send DECIMALS to API) ----------
  async function runAnalysis() {
    if (!mappingValid) { setError(REQUIRED_NOTE); return; }
    setError(""); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping", JSON.stringify(mapping));

      // Convert % UI values → decimals for backend
      const thresholdDec  = Number(threshold || 0) / 100;
      const sizeTolDec    = Number(sizeTol   || 0) / 100;
      const taxRateDec    = Number(taxRate   || 0) / 100;

      fd.append("threshold_pct", String(thresholdDec));
      fd.append("prefer_market", "true");
      fd.append("min_units", String(minUnits));
      fd.append("size_tolerance", String(sizeTolDec));
      fd.append("year_tolerance", String(yearTol));
      fd.append("min_comps", String(minComps));
      fd.append("min_subject_value", String(minSubjectValue));
      fd.append("tax_rate", String(taxRateDec));

      const res = await api.post("/eu/analyze", fd);
      const data = res.data || {};
      setResult(data);

      const sel = {};
      (data.flagged || []).forEach((row, idx) => {
        const key = row?.subject?.id || `row-${idx}`;
        sel[key] = (row?.comps || []).map(() => true);
      });
      setSelections(sel);
      setExpandedKey(null);
      setSlide(3);
    } catch (err) {
      setError(err?.response?.data?.detail || "Analyze failed");
      setResult(null);
    } finally {
      setBusy(false);
    }
  }

  // ---------- recompute with selected comps (use DECIMAL tax) ----------
  const computeFromSelection = useCallback((row, selArr) => {
    const chosen = (row?.comps || []).filter((_, i) => selArr?.[i]);
    if (!chosen.length) return { stats: row.stats, chosen, flagged: false };
    const median = medianOf(chosen.map((c) => Number(c.per_unit || 0)));
    const perUnitSubj = Number(row?.subject?.per_unit || 0);
    const overBy = perUnitSubj - median;
    const overPct = median > 0 ? overBy / median : 0;
    const threshold_pct = Number(row?.stats?.threshold_pct || 0);
    const flagged = perUnitSubj > median * (1 + threshold_pct);
    const sizeSub = Number(row?.subject?.units_or_area || 0);
    const estReduction = Math.max(0, overBy * sizeSub);
    const taxRt = Number(taxRate || 0) / 100;   // decimal
    const unit_label = row?.stats?.unit_label || "$/Unit";
    const newStats = {
      ...row.stats,
      median_per_unit: median,
      cutoff_per_unit: median * (1 + threshold_pct),
      over_pct: overPct,
      delta_per_unit: overBy,
      unit_label,
      value_basis: "market_value",
      est_value_reduction: estReduction,
      est_tax_savings: estReduction * taxRt,
      tax_rate: taxRt,
    };
    return { stats: newStats, chosen, flagged };
  }, [taxRate]);

  function medianOf(nums) {
    const arr = nums.filter((n) => !isNaN(n)).slice().sort((a,b)=>a-b);
    if (!arr.length) return 0;
    const m = Math.floor(arr.length/2);
    return arr.length % 2 ? arr[m] : (arr[m-1]+arr[m])/2;
  }

  const getDerived = useCallback((row, idx) => {
    const key = row?.subject?.id || `row-${idx}`;
    const sel = selections[key];
    if (!sel) return { stats: row.stats, chosen: row.comps, flagged: row.flagged };
    return computeFromSelection(row, sel);
  }, [selections, computeFromSelection]);

  const toggleComp = (row, idxRow, idxComp) => {
    const key = row?.subject?.id || `row-${idxRow}`;
    setSelections((prev) => {
      const arr = (prev[key] || (row.comps || []).map(() => true)).slice();
      arr[idxComp] = !arr[idxComp];
      return { ...prev, [key]: arr };
    });
  };
  const setAllComps = (row, idxRow, checked) => {
    const key = row?.subject?.id || `row-${idxRow}`;
    setSelections((prev) => ({ ...prev, [key]: (row.comps || []).map(() => checked) }));
  };

  // ---------- flyer (delegates to pdf.js) ----------
  async function downloadFlyer(row, idxRow) {
    setError("");
    try {
      await createAndDownloadFlyer(row, idxRow, getDerived, taxRate);
    } catch (err) {
      setError(err?.message || "PDF failed");
    }
  }

  const flagged = result?.flagged || [];

  /* =========================
     RENDER — Carousel
     ========================= */
  return (
    <div className="min-h-screen text-white relative overflow-hidden" style={{ backgroundColor: NAVY_BG }}>
      <FXBackground />

      {/* Carousel track */}
      <div
        className="relative h-[100svh] w-[400vw] flex transition-transform duration-500 ease-[cubic-bezier(.2,.7,.2,1)]"
        style={{ transform: `translateX(-${slide * 100}vw)` }}
      >
        {/* Slide 0 — Upload */}
        <SlideWrap>
          <CarouselCard title="Upload" subtitle="Load your Excel/CSV and detect headers." step="1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Excel/CSV file</Label>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="text-sm w-full file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-white/10 file:text-white hover:file:bg-white/20"
                />
              </div>
              <div className="self-end">
                <ActionButton onClick={previewColumns} disabled={busy || !file}>
                  {busy ? "Uploading…" : "Upload"}
                </ActionButton>
              </div>
            </div>
            {columns.length > 0 && <Note>{`Detected ${columns.length} columns.`}</Note>}
            {error && <ErrorNote>{error}</ErrorNote>}
          </CarouselCard>
        </SlideWrap>

        {/* Slide 1 — Map Columns */}
        <SlideWrap>
          <CarouselCard title="Map Columns" subtitle="Map fields required for analysis. Market value is mandatory." step="2">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <ActionButton onClick={useGuessed}>Use guessed mapping</ActionButton>
              <ActionButton onClick={copyMappingJSON}>Copy mapping JSON</ActionButton>
              <label className="rounded-lg px-3 py-1.5 text-sm border border-white/10 hover:bg-white/5 cursor-pointer" style={{ color: SLATE_TEXT }}>
                Load mapping JSON
                <input type="file" accept=".json" onChange={loadMappingFromFile} className="hidden" />
              </label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(FIELD_LABELS).map(([key, label]) => (
                <div key={key}>
                  <Label>{label}</Label>
                  <select
                    className="w-full rounded-lg border border-white/10 bg-[#0E1E41] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white/20"
                    style={{ color: SLATE_TEXT }}
                    value={mapping[key] || ""}
                    onChange={(e) => setMapKey(key, e.target.value || null)}
                    disabled={columns.length === 0}
                  >
                    <option value="">—</option>
                    {columns.map((c) => (
                      <option key={`${key}-${c}`} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <Note>{REQUIRED_NOTE}</Note>
          </CarouselCard>
        </SlideWrap>

        {/* Slide 2 — Settings & Run */}
        <SlideWrap>
          <CarouselCard title="Analysis Settings" subtitle="Tune thresholds, filters, and tax rate." step="3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <PercentInput label="Threshold" value={threshold} onChange={setThreshold} min={0} max={100} step={0.1}/>
              <InputN        label="Min size / units" value={minUnits} onChange={setMinUnits} min={1} step={1}/>
              <PercentInput label="Size tolerance" value={sizeTol} onChange={setSizeTol} min={0} max={100} step={0.5}/>
              <InputN        label="Year tolerance (± years)" value={yearTol} onChange={setYearTol} min={0} max={50} step={1}/>
              <InputN        label="Minimum comps required" value={minComps} onChange={setMinComps} min={3} max={30} step={1}/>
              <MoneyInput    label="Minimum subject value (Market)" value={minSubjectValue} onChange={setMinSubjectValue} min={0} step={1000}/>
              <PercentInput  label="Estimated tax rate" value={taxRate} onChange={setTaxRate} min={0} max={100} step={0.01}/>
            </div>

            <div className="mt-4 flex items-center gap-3">
              <ActionButton onClick={runAnalysis} disabled={busy || !mappingValid}
                title={!mappingValid ? REQUIRED_NOTE : ""}>
                {busy ? "Analyzing…" : "Run Analysis"}
              </ActionButton>
              {error && <ErrorNote>{error}</ErrorNote>}
            </div>
          </CarouselCard>
        </SlideWrap>

        {/* Slide 3 — Analysis (now wider + stacked controls) */}
        <section className="w-screen h-[100svh] flex items-center justify-center relative">
          <div
            className="w-[94vw] max-w-[1600px] min-w-[320px] max-h-[80vh] overflow-y-auto rounded-[28px]"
            style={{ transformStyle: "preserve-3d" }}
          >
            <AnalysisCard
              result={result}
              mapping={mapping}
              selections={selections}
              setSelections={setSelections}
              expandedKey={expandedKey}
              setExpandedKey={setExpandedKey}
              getDerived={getDerived}
              toggleComp={toggleComp}
              setAllComps={setAllComps}
              downloadFlyer={downloadFlyer}
              // NEW controls
              sortKey={sortKey}
              setSortKey={setSortKey}
              sortDir={sortDir}
              setSortDir={setSortDir}
              searchText={searchText}
              setSearchText={setSearchText}
              filterMinComps={filterMinComps}
              setFilterMinComps={setFilterMinComps}
              filterMinOverPct={filterMinOverPct}
              setFilterMinOverPct={setFilterMinOverPct}
              filterMinTotal={filterMinTotal}
              setFilterMinTotal={setFilterMinTotal}
              filterMaxTotal={filterMaxTotal}
              setFilterMaxTotal={setFilterMaxTotal}
              filterAgent={filterAgent}
              setFilterAgent={setFilterAgent}
              filterCounty={filterCounty}
              setFilterCounty={setFilterCounty}
              filterType={filterType}
              setFilterType={setFilterType}
              filterGroup={filterGroup}
              setFilterGroup={setFilterGroup}
              clearFilters={clearFilters}
              sortOpts={SORT_OPTS}
            />
          </div>
        </section>
      </div>

      {/* Fixed bottom NavBar (global, clickable) */}
      <NavBar
        leftDisabled={leftDisabled || slide === 0}
        rightDisabled={!rightEnabled}
        glowRight={rightEnabled}
        onLeft={goPrev}
        onRight={goNext}
        slide={slide}
        total={result ? 4 : 3}
      />

      {/* Floating Apps pill (bottom-right) */}
      <button
        onClick={onClose}
        className="fixed bottom-5 right-5 z-50 rounded-2xl bg:white text-slate-900 px-4 py-2 shadow-[0_10px_30px_rgba(0,0,0,0.25)] border border-black/10 hover:shadow-[0_14px_40px_rgba(0,0,0,0.35)] bg-white"
        title="Back to Apps"
      >
        <span className="inline-flex items-center gap-2 text-sm">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="3" width="7" height="7" rx="2" fill="currentColor"/>
            <rect x="14" y="3" width="7" height="7" rx="2" fill="currentColor"/>
            <rect x="3" y="14" width="7" height="7" rx="2" fill="currentColor"/>
            <rect x="14" y="14" width="7" height="7" rx="2" fill="currentColor"/>
          </svg>
          Apps
        </span>
      </button>

      {/* Local CSS */}
      <style>{cssFX}</style>
    </div>
  );
}

/* =========================
   Slides & Cards (visual only)
   ========================= */
function SlideWrap({ children }) {
  return (
    <section className="w-screen h-[100svh] flex items-center justify-center relative">
      <div className="w-[75vw] max-w-5xl min-w-[320px]" style={{ transformStyle: "preserve-3d" }}>
        {children}
      </div>
    </section>
  );
}

function CarouselCard({ step, title, subtitle, children }) {
  return (
    <div
      className="relative rounded-[28px] border border-white/12 overflow-hidden shadow-[0_25px_80px_rgba(0,0,0,0.45)]"
      style={{ background: `linear-gradient(180deg, ${NAVY_SOFT} 0%, ${NAVY_CARD} 100%)` }}
    >
      {/* glow corners */}
      <span className="pointer-events-none absolute -inset-10 opacity-40"
            style={{ background: "radial-gradient(600px 200px at 0% 0%, rgba(255,255,255,0.16), transparent 60%)", filter: "blur(18px)" }} />
      <div className="relative z-10 p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-9 w-9 rounded-xl grid place-items-center text-sm font-semibold ring-1 ring-white/15"
               style={{ backgroundColor: NAVY_SOFT, color: VANILLA }}>{step}</div>
          <div className="leading-tight">
            <h2 className="text-xl font-semibold" style={{ color: VANILLA }}>{title}</h2>
            {subtitle && <div className="text-xs" style={{ color: MUTED }}>{subtitle}</div>}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

function AnalysisCard({
  result,
  mapping,
  selections,
  setSelections,
  expandedKey,
  setExpandedKey,
  getDerived,
  toggleComp,
  setAllComps,
  downloadFlyer,
  // NEW props:
  sortKey, setSortKey, sortDir, setSortDir,
  searchText, setSearchText,
  filterMinComps, setFilterMinComps,
  filterMinOverPct, setFilterMinOverPct,
  filterMinTotal, setFilterMinTotal,
  filterMaxTotal, setFilterMaxTotal,
  filterAgent, setFilterAgent,
  filterCounty, setFilterCounty,
  filterType, setFilterType,
  filterGroup, setFilterGroup,
  clearFilters, sortOpts
}) {
  const rawRows = result?.flagged || [];

  // Build derived records for sorting/filtering
  const records = useMemo(() => {
    return rawRows.map((row, idxRow) => {
      const { stats, chosen } = getDerived(row, idxRow);
      const subj = row.subject || {};
      const key = subj.id || `row-${idxRow}`;
      return {
        key,
        idxRow,
        row,
        stats,
        chosen,
        unitLabel: stats?.unit_label || "$/Unit",
        perUnit: Number(subj.per_unit || 0),
        totalValue: safeNum(subj.total_value),
        medianPerUnit: Number(stats?.median_per_unit || 0),
        overPct: Number(stats?.over_pct || 0), // decimal (0.27 -> 27%)
        size: Number(subj.units_or_area || 0),
        year: safeNum(subj.year_built),
        compsCount: (row.comps || []).length,
        chosenCount: (chosen || []).length,
        owner: (subj.owner || "").toString(),
        agent: (subj.agent || "").toString(),
        county: (subj.county || "").toString(),
        type: (subj.type || "").toString(),
        group: (subj.group_key || "").toString(),
        id: (subj.id || "").toString(),
        address: (subj.address || "").toString(),
      };
    });
  }, [rawRows, getDerived]);

  // Filters
  const filtered = useMemo(() => {
    const text = (searchText || "").trim().toLowerCase();
    const minComps = asInt(filterMinComps, -Infinity);
    const minOver = (filterMinOverPct === "" ? -Infinity : Number(filterMinOverPct)/100);
    const minTot  = (filterMinTotal === "" ? -Infinity : Number(filterMinTotal));
    const maxTot  = (filterMaxTotal === "" ?  Infinity : Number(filterMaxTotal));

    const like = (needle, hay) => !needle || hay.toLowerCase().includes(needle.toLowerCase());

    return records.filter(r => {
      const passText = !text || [
        r.id, r.owner, r.address, r.agent, r.county, r.type, r.group
      ].some(s => (s || "").toLowerCase().includes(text));

      const passComps = Number.isFinite(minComps) ? (r.compsCount >= minComps) : true;
      const passOver  = Number.isFinite(minOver) ? (r.overPct >= minOver) : true;
      const passTot   = (r.totalValue >= minTot) && (r.totalValue <= maxTot);

      const passAgent = like(filterAgent, r.agent);
      const passCounty= like(filterCounty, r.county);
      const passType  = like(filterType, r.type);
      const passGroup = like(filterGroup, r.group);

      return passText && passComps && passOver && passTot && passAgent && passCounty && passType && passGroup;
    });
  }, [
    records, searchText, filterMinComps, filterMinOverPct, filterMinTotal, filterMaxTotal,
    filterAgent, filterCounty, filterType, filterGroup
  ]);

  // Sorting
  const sorted = useMemo(() => {
    const dir = (sortDir === "asc") ? 1 : -1;
    const get = (r) => {
      switch (sortKey) {
        case "overPct": return r.overPct;
        case "totalValue": return r.totalValue;
        case "perUnit": return r.perUnit;
        case "medianPerUnit": return r.medianPerUnit;
        case "size": return r.size;
        case "year": return r.year || -Infinity;
        case "compsCount": return r.compsCount;
        case "chosenCount": return r.chosenCount;
        case "owner": return r.owner.toLowerCase();
        case "agent": return r.agent.toLowerCase();
        case "county": return r.county.toLowerCase();
        case "type": return r.type.toLowerCase();
        case "group": return r.group.toLowerCase();
        default: return r.overPct;
      }
    };
    return filtered.slice().sort((a,b) => {
      const av = get(a), bv = get(b);
      if (av === bv) return 0;
      if (typeof av === "string" && typeof bv === "string") return av.localeCompare(bv) * dir;
      return ((av || 0) < (bv || 0) ? -1 : 1) * dir;
    });
  }, [filtered, sortKey, sortDir]);

  return (
    <div
      className="relative rounded-[28px] border border-white/12 overflow-hidden shadow-[0_25px_80px_rgba(0,0,0,0.5)]"
      style={{ background: `linear-gradient(180deg, ${NAVY_SOFT} 0%, ${NAVY_CARD} 100%)`, transform: "translateZ(20px)" }}
    >
      <div className="relative z-10 p-6 sm:p-8">
        <h2 className="text-xl font-semibold mb-1" style={{ color: VANILLA }}>Analysis</h2>
        <div className="text-xs mb-4" style={{ color: MUTED }}>
          Mapping used: {Object.entries(mapping).map(([k, v]) => `${k}:${v || "—"}`).join(" • ")}
        </div>

        {/* NEW: Stacked, grouped controls */}
        <div className="space-y-3 mb-4">
          {/* Group 1: Search & Sort */}
          <Fieldset title="Search & Sort">
            <div className="grid grid-cols-12 gap-3 items-end">
              <div className="col-span-12 xl:col-span-8">
                <InputText
                  label="Search (id/owner/address/agent/county/type/group)"
                  value={searchText}
                  onChange={setSearchText}
                  placeholder="Start typing…"
                />
              </div>
              <div className="col-span-6 sm:col-span-4 xl:col-span-3">
                <Label>Sort</Label>
                <select
                  className="w-full rounded-lg border border-white/10 bg-[#0E1E41] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white/20"
                  style={{ color: SLATE_TEXT }}
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                >
                  {sortOpts.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </div>
              <div className="col-span-6 sm:col-span-2 xl:col-span-1">
                <Label>&nbsp;</Label>
                <button
                  onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")}
                  className="w-full rounded-lg px-3 py-2 text-sm border border-white/10 hover:bg-white/5"
                  title="Toggle sort direction"
                  style={{ color: VANILLA }}
                >
                  {sortDir === "asc" ? "↑" : "↓"}
                </button>
              </div>
            </div>
          </Fieldset>

          {/* Group 2: Value & Counts */}
          <Fieldset title="Value & Counts">
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-6 sm:col-span-3 xl:col-span-2">
                <InputN label="Min comps" value={filterMinComps} onChange={setFilterMinComps} min={0} step={1}/>
              </div>
              <div className="col-span-6 sm:col-span-3 xl:col-span-2">
                <PercentInput label="Over % ≥" value={filterMinOverPct} onChange={setFilterMinOverPct} min={0} max={200} step={0.1}/>
              </div>
              <div className="col-span-6 sm:col-span-3 xl:col-span-2">
                <MoneyInput label="Min Total" value={filterMinTotal} onChange={setFilterMinTotal} min={0} step={1000}/>
              </div>
              <div className="col-span-6 sm:col-span-3 xl:col-span-2">
                <MoneyInput label="Max Total" value={filterMaxTotal} onChange={setFilterMaxTotal} min={0} step={1000}/>
              </div>
            </div>
          </Fieldset>

          {/* Group 3: Attributes */}
          <Fieldset title="Attributes">
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-12 sm:col-span-6 xl:col-span-3">
                <InputText label="Agent contains" value={filterAgent} onChange={setFilterAgent} />
              </div>
              <div className="col-span-12 sm:col-span-6 xl:col-span-3">
                <InputText label="County contains" value={filterCounty} onChange={setFilterCounty} />
              </div>
              <div className="col-span-12 sm:col-span-6 xl:col-span-3">
                <InputText label="Type contains" value={filterType} onChange={setFilterType} />
              </div>
              <div className="col-span-12 sm:col-span-6 xl:col-span-3">
                <InputText label="Group contains" value={filterGroup} onChange={setFilterGroup} />
              </div>
            </div>
          </Fieldset>

          {/* Footer actions for filters */}
          <div className="flex items-center justify-between">
            <ActionButton onClick={clearFilters}>Clear filters</ActionButton>
            <span className="text-xs" style={{ color: MUTED }}>
              Showing <b>{sorted.length}</b> of {rawRows.length} flagged subjects
            </span>
          </div>
        </div>

        {(!result || (rawRows || []).length === 0) ? (
          <div className="text-sm" style={{ color: SLATE_TEXT }}>
            No properties flagged above the selected threshold.
          </div>
        ) : (
          <div className="space-y-5">
            {sorted.map(({ row, idxRow, stats, chosen, key }) => {
              const unitLabel = stats?.unit_label || "$/Unit";
              const overPctDisp = `${((stats?.over_pct || 0) * 100).toFixed(1)}%`;
              const medDisp = `$${(stats?.median_per_unit || 0).toFixed(2)}`;
              const perDisp = `$${(row?.subject?.per_unit || 0).toFixed(2)}`;
              const totalDisp = row?.subject?.total_value != null ? `$${Number(row.subject.total_value).toLocaleString()}` : "—";
              const chosenCount = chosen?.length || 0;
              const flagged = (row?.flagged ?? true);

              return (
                <div key={key} className="rounded-2xl border border-white/10 overflow-hidden">
                  {/* Header */}
                  <div
                    role="button"
                    tabIndex={0}
                    className="w-full text-left px-4 sm:px-5 py-4 grid grid-cols-12 gap-3 items-center hover:bg-white/5"
                    onClick={() => setExpandedKey(expandedKey === key ? null : key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setExpandedKey(expandedKey === key ? null : key);
                      }
                    }}
                  >
                    <div className="col-span-12 md:col-span-3">
                      <div className="font-medium" style={{ color: VANILLA }}>{row?.subject?.id || "—"}</div>
                      <div className="text-xs" style={{ color: MUTED }}>{row?.subject?.owner || "—"}</div>
                      <div className="text-xs" style={{ color: MUTED }}>{row?.subject?.address || "—"}</div>
                      {row?.subject?.agent ? (
                        <div className="text-[11px]" style={{ color: MUTED }}>Agent: {row.subject.agent}</div>
                      ) : null}
                      {row?.subject?.deed_date ? (
                        <div className="text-[11px]" style={{ color: MUTED }}>Deed date: {row.subject.deed_date}</div>
                      ) : null}
                    </div>
                    <Cell className="md:col-span-2">{row?.subject?.type || "—"} • {row?.subject?.year_built || "—"}</Cell>
                    <Cell className="md:col-span-2">Size: {(row?.subject?.units_or_area || 0).toLocaleString()}</Cell>
                    <Cell className="md:col-span-2">{unitLabel}: {perDisp}</Cell>
                    <Cell className="md:col-span-1">Median: {medDisp}</Cell>
                    <Cell className="md:col-span-1">Total: {totalDisp}</Cell>
                    <div className="col-span-12 md:col-span-1">
                      <span className={[
                        "inline-block text-xs px-2 py-1 rounded-md border",
                        flagged ? "bg-red-500/20 text-red-200 border-red-400/30" : "bg-green-500/20 text-green-200 border-green-400/30",
                      ].join(" ")}>{overPctDisp}</span>
                    </div>
                  </div>

                  {/* Expanded: comps editor */}
                  {expandedKey === key && (
                    <div className="px-4 sm:px-5 pb-5">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
                        <div className="text-sm" style={{ color: SLATE_TEXT }}>
                          Selected comps: <span className="font-semibold ml-1" style={{ color: VANILLA }}>{chosenCount}</span>
                          <span className="ml-3 text-xs" style={{ color: MUTED }}>
                            (Available: {(row.comps || []).length})
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <ActionButton onMouseDown={(e)=>e.stopPropagation()} onClick={() => setAllComps(row, idxRow, true)}>Select all</ActionButton>
                          <ActionButton onMouseDown={(e)=>e.stopPropagation()} onClick={() => setAllComps(row, idxRow, false)}>Clear all</ActionButton>
                          <ActionButton onMouseDown={(e)=>e.stopPropagation()} onClick={() => downloadFlyer(row, idxRow)} disabled={!chosenCount}>Download PDF</ActionButton>
                        </div>
                      </div>

                      <div className="overflow-x-auto rounded-xl border border-white/10">
                        <table className="min-w-full text-sm">
                          <thead className="bg-white/5">
                            <tr className="text-left" style={{ color: MUTED }}>
                              <th className="py-2 pl-3 pr-3">Use</th>
                              <th className="py-2 pr-3">Address / Owner</th>
                              <th className="py-2 pr-3">Type</th>
                              <th className="py-2 pr-3">Size</th>
                              <th className="py-2 pr-3">Year</th>
                              <th className="py-2 pr-3">{unitLabel}</th>
                              <th className="py-2 pr-3">Total</th>
                            </tr>
                          </thead>
                          <tbody
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {(row.comps || []).map((c, i) => {
                              const used = (selections[row?.subject?.id || `row-${idxRow}`] || [])[i] ?? true;
                              const toggleThis = (e) => { e.stopPropagation(); toggleComp(row, idxRow, i); };
                              const onRowKey = (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleThis(e); } };
                              return (
                                <tr
                                  key={i}
                                  role="checkbox"
                                  aria-checked={!!used}
                                  tabIndex={0}
                                  onKeyDown={onRowKey}
                                  onClick={toggleThis}
                                  className={[
                                    "border-t border-white/10 align-top hover:bg-white/[0.03] cursor-pointer",
                                    used ? "" : "opacity-65"
                                  ].join(" ")}
                                >
                                  <td className="py-2 pl-3 pr-3">
                                    <input
                                      type="checkbox"
                                      checked={!!used}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onTouchStart={(e) => e.stopPropagation()}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={toggleThis}
                                      className="h-4 w-4 accent-white align-middle"
                                    />
                                  </td>
                                  <td className="py-2 pr-3">
                                    <div className="font-medium" style={{ color: SLATE_TEXT }}>
                                      {c.address || c.owner || c.id || "—"}
                                    </div>
                                    <div className="text-xs" style={{ color: MUTED }}>
                                      {c.owner ? `${c.owner}` : ""}{c.owner && c.id ? " • " : ""}{c.id || ""}
                                    </div>
                                  </td>
                                  <td className="py-2 pr-3" style={{ color: SLATE_TEXT }}>{c.type || "—"}</td>
                                  <td className="py-2 pr-3" style={{ color: SLATE_TEXT }}>
                                    {c.units_or_area != null ? Number(c.units_or_area).toLocaleString() : "—"}
                                  </td>
                                  <td className="py-2 pr-3" style={{ color: SLATE_TEXT }}>{c.year_built || "—"}</td>
                                  <td className="py-2 pr-3" style={{ color: SLATE_TEXT }}>${(c.per_unit || 0).toFixed(2)}</td>
                                  <td className="py-2 pr-3" style={{ color: SLATE_TEXT }}>
                                    {c.value != null ? `$${Number(c.value).toLocaleString()}` : "—"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* KPIs */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                        <Kpi label={`${stats?.unit_label || "$/Unit"} (Subject)`} value={`$${(row?.subject?.per_unit || 0).toFixed(2)}`} />
                        <Kpi label={`Group Median ${stats?.unit_label || "$/Unit"}`} value={`$${(stats?.median_per_unit || 0).toFixed(2)}`} muted />
                        <Kpi label={"Over vs Median"} value={`${((stats?.over_pct || 0) * 100).toFixed(1)}%`} state={(stats?.over_pct || 0) > 0 ? "bad" : "ok"} />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                        <Kpi label={"Estimated reduction to median"} value={`$${Math.round(stats?.est_value_reduction || 0).toLocaleString()}`} />
                        <Kpi label={"Estimated tax savings"} value={`$${Math.round(stats?.est_tax_savings || 0).toLocaleString()}`} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* =========================
   Presentational bits
   ========================= */
function Label({ children }) {
  return <label className="block text-xs mb-1" style={{ color: MUTED }}>{children}</label>;
}
function Note({ children }) {
  return <div className="text-xs mt-2" style={{ color: MUTED }}>{children}</div>;
}
function ErrorNote({ children }) {
  return <div className="text-sm mt-2 text-red-300">{children}</div>;
}
function ActionButton({ children, disabled, onClick, title }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="rounded-lg px-4 py-2 text-sm border border-white/10 hover:bg-white/5 disabled:opacity-50"
      style={{ color: VANILLA }}
    >
      {children}
    </button>
  );
}
function Cell({ children, className }) {
  return <div className={`col-span-12 text-sm ${className || ""}`} style={{ color: SLATE_TEXT }}>{children}</div>;
}

/* Number input with suffix (%, $) */
function InputWithSuffix({ label, value, onChange, min, max, step, suffix, placeholder }) {
  return (
    <div className="relative">
      <Label>{label}</Label>
      <input
        type="number"
        min={min} max={max} step={step}
        className="w-full rounded-lg border border-white/10 bg-[#0E1E41] px-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white/20"
        style={{ color: SLATE_TEXT }}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      />
      <span className="pointer-events-none absolute right-3 top-[32px] text-sm" style={{ color: MUTED }}>
        {suffix}
      </span>
    </div>
  );
}
function PercentInput(props) {
  return <InputWithSuffix {...props} suffix="%" />;
}
function MoneyInput(props) {
  return <InputWithSuffix {...props} suffix="$" />;
}

/* Plain inputs */
function InputN({ label, value, onChange, min, max, step }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type="number" min={min} max={max} step={step}
        className="w-full rounded-lg border border-white/10 bg-[#0E1E41] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white/20"
        style={{ color: SLATE_TEXT }}
        value={value}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      />
    </div>
  );
}
function InputText({ label, value, onChange, placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type="text"
        className="w-full rounded-lg border border-white/10 bg-[#0E1E41] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-white/20"
        style={{ color: SLATE_TEXT }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function Kpi({ label, value, muted = false, state }) {
  const base = "rounded-xl px-4 py-3 border";
  const color =
    state === "bad" ? "border-red-400/30 bg-red-500/10 text-red-100"
    : state === "ok" ? "border-green-400/30 bg-green-500/10 text-green-100"
    : "border-white/10 bg-white/5 text-white";
  return (
    <div className={`${base} ${color}`}>
      <div className="text-xs" style={{ color: muted ? "#9BB0D0" : "#E9EEF8" }}>{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

/* Group shell for filters */
function Fieldset({ title, children }) {
  return (
    <div className="rounded-xl border border-white/10 p-3 bg-white/[0.03]">
      <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: MUTED }}>
        {title}
      </div>
      {children}
    </div>
  );
}

/* Fixed Navigation strip (bottom center) */
function NavBar({ leftDisabled, rightDisabled, glowRight, onLeft, onRight, slide, total }) {
  return (
    <div className="fixed bottom-5 left-0 right-0 z-50 flex items-center justify-center gap-6">
      <button
        onClick={onLeft}
        disabled={leftDisabled}
        className="rounded-full h-11 w-11 grid place-items-center border border-white/15 bg-white/10 hover:bg-white/15 disabled:opacity-40"
        title="Previous"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M15 6l-6 6 6 6" stroke="white" strokeWidth="2" strokeLinecap="round"/></svg>
      </button>

      <div className="flex items-center gap-2">
        {Array.from({ length: total }).map((_, i) => (
          <span key={i} className={`h-2 w-2 rounded-full ${i === slide ? "bg-white" : "bg-white/30"}`} />
        ))}
      </div>

      <button
        onClick={onRight}
        disabled={rightDisabled}
        className={[
          "rounded-full h-11 w-11 grid place-items-center border border-white/15 bg-white/10 hover:bg-white/15 disabled:opacity-40",
          glowRight ? "shadow-[0_0_0_6px_rgba(255,255,255,0.12)] ring-2 ring-white/40 animate-pulse" : ""
        ].join(" ")}
        title="Next"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 6l6 6-6 6" stroke="white" strokeWidth="2" strokeLinecap="round"/></svg>
      </button>
    </div>
  );
}

/* Background FX (pure CSS) */
function FXBackground() {
  return (
    <>
      {/* subtle noise */}
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.07] mix-blend-soft-light"
        style={{ backgroundImage:
          "url(\"data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3CfeComponentTransfer%3E%3CfeFuncA type='linear' slope='0.25'/%3E%3C/feComponentTransfer%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.7'/%3E%3C/svg%3E\")" }}
      />
      {/* scanlines */}
      <div className="pointer-events-none fixed inset-0 opacity-[0.04] bg-[linear-gradient(to_bottom,rgba(255,255,255,0.15)_1px,transparent_1px)] bg-[length:100%_22px]" />
      {/* soft spotlight */}
      <div className="pointer-events-none fixed inset-0" style={{ background: "radial-gradient(600px 300px at 50% -10%, rgba(255,255,255,0.16), transparent 60%)" }} />
    </>
  );
}

const cssFX = `
button:focus-visible { outline: none; box-shadow: 0 0 0 2px rgba(255,255,255,0.18); }
`;

/* ===== helpers ===== */
function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
function asInt(v, fallback=0) {
  if (v === "" || v === null || v === undefined) return fallback;
  const n = Number(v);
  return Number.isInteger(n) ? n : Math.round(n);
}
